import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { mdiBellRingOutline, mdiEmailOutline, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import Button from "components/Button";
import Checkbox from "components/Checkbox";
import { showSuccessMessage, showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { NotificationsFormData, UserSettingsData, UserSettingsFormData } from "models/UserSetting";
import { updateUserSettingsAPI } from "services/api/userSetting";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import { NOTIFICATIONS_ACTIVITY_ITEMS } from "../ActivityList";
import styles from "./style.module.scss";

interface IActivitySettingsFormProps {
  defaultValue: UserSettingsData;
}

export default function ActivitySettingsForm({
  defaultValue
}: Readonly<IActivitySettingsFormProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const { isDesktop } = useDeviceType();
  const { userDetails } = useSelector((state: RootState) => state.userState);

  const {
    register,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<Partial<NotificationsFormData>>({
    values: { ...defaultValue }
  });

  const onSubmit = async (data: UserSettingsFormData) => {
    const formData = {
      id: defaultValue.id,
      user: defaultValue.user,
      unit: defaultValue.unit,
      ...data
    } as UserSettingsData;

    dispatch(handleLoading(true));
    await updateUserSettingsAPI(formData)
      .then(() => {
        showSuccessMessage("Settings updated successfully.");
      })
      .catch(() => {
        showErrorMessage("Error update shipper settings");
      })
      .finally(() => dispatch(handleLoading(false)));
  };

  return (
    <form className={styles.activityForm} onSubmit={handleSubmit(onSubmit)}>
      <ul className={clsx(styles.list, "mt-3")}>
        <li className={styles.header}>
          <span className={styles.title}>Activity Notifications</span>
          <Icon path={mdiEmailOutline} className="flex-none size-5 desktop:size-6" />
          <Icon path={mdiBellRingOutline} className={styles.bellIcon} />
        </li>

        {NOTIFICATIONS_ACTIVITY_ITEMS()
          .filter((activityList) => !activityList.hidden)
          .map((item) => (
            <li key={item.value} className={styles.noticeBody}>
              <span className={styles.tableRow}>{item.label}</span>
              <Checkbox
                name={item.value as any}
                register={register}
                wrapperClass="-mr-1.5 desktop:-mr-0.5"
                {...(!userDetails?.has_verified_email && { disabled: true })}
              />
              <Checkbox
                name={`notify_${item.value}` as any}
                register={register}
                wrapperClass="-mr-1.5 desktop:-mr-0.5"
                {...(!userDetails?.has_verified_email && { disabled: true })}
              />
            </li>
          ))}
      </ul>

      <Button
        type="submit"
        variant={isDesktop ? "outline" : "contained"}
        className={styles.submit}
        disabled={!userDetails?.has_verified_email || isSubmitting}>
        {isSubmitting ? <Icon path={mdiLoading} size={1.5} spin={1} /> : "Save"}
      </Button>
    </form>
  );
}
